import React, { useState, useEffect } from 'react'
import './EditUserModal.css'
import { User } from '../../interfaces/definitions/user.interface'
import { UserRole } from '../../interfaces/definitions/user-role.enum'
import { roleData } from './AdminPage'

interface EditUserModalProps {
  user?: User
  onClose: () => void
  onSave: (user: Partial<User>) => void
  onDelete?: () => void
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSave, onDelete }) => {
  const [username, setUsername] = useState(user?.username ?? '')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>(user?.role as UserRole ?? UserRole.INCOMING)
  const [admin, setAdmin] = useState(user?.admin ?? false)
  const [passwordValidation, setPasswordValidation] = useState({
    hasNumber: false,
    hasSpecialChar: false,
    hasCapitalLetter: false,
    hasLowercaseLetter: false,
    hasAtLeastEightChars: false
  })

  useEffect(() => {
    if (user != null) {
      setUsername(user.username)
      setRole(user.role)
      setAdmin(user.admin)
    }
  }, [user])

  const validatePassword = (value: string): {
    hasNumber: boolean
    hasSpecialChar: boolean
    hasCapitalLetter: boolean
    hasLowercaseLetter: boolean
    hasAtLeastEightChars: boolean
  } => {
    return {
      hasNumber: /\d/.test(value),
      hasSpecialChar: /[#?!@$%^*-.]/g.test(value),
      hasCapitalLetter: /[A-Z]/g.test(value),
      hasLowercaseLetter: /[a-z]/g.test(value),
      hasAtLeastEightChars: value.length >= 8
    }
  }

  const handlePasswordChange = (value: string): void => {
    setPassword(value)
    setPasswordValidation(validatePassword(value))
  }

  const checkPasswordRegex = (): boolean => {
    const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^*-.]).{8,}$/
    return regex.test(password)
  }

  const disableAddUpdateButton = (): boolean => {
    if (user != null) {
      return (!checkPasswordRegex() && password.length !== 0) || role.length === 0
    } else {
      return username.length === 0 || !checkPasswordRegex() || role.length === 0
    }
  }

  const handleSave = (): void => {
    onSave({ username, password, role, admin })
  }

  useEffect(() => {
    const edited = password !== '' || role !== user?.role || admin !== user?.admin

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (edited && event.key === 'Enter') {
        handleSave()
      } else if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, handleSave]) // Dependencies array includes onClose and handleSave

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }
  const handleModalContentClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation()
  }

  return (
    <div className='modal-overlay' onClick={handleOverlayClick}>
      <div className='modal-content' onClick={handleModalContentClick}>
        <h2>{(user != null) ? 'Edit User' : 'Create User'}</h2>
        <input
          type='text'
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder='Username'
          disabled={!(user == null)}
        />
        <div className='password-field-container'>
          <input
            type='password'
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            placeholder='Password'
          />
          {password.length > 0 && (
            <RenderPasswordCriteria passwordValidation={passwordValidation} />
          )}
        </div>
        <p className='checkbox-text'>Step Permissions:</p>
        <div className='role-checkboxes'>
          {roleData.map((step) => (
            <div key={step.value} className='role-checkbox-item'>
              <label>
                <input
                  type='checkbox'
                  checked={role === step.value}
                  onChange={() => setRole(step.value)}
                />
                {step.label}
              </label>
            </div>
          ))}
        </div>
        <p className='checkbox-text'>Account Permissions:</p>
        <label className='admin-checkbox'>
          <input
            type='checkbox'
            checked={admin}
            onChange={(e) => setAdmin(e.target.checked)}
          />
          Admin
        </label>
        <div className='button-group'>
          <button className='add-update-button' onClick={handleSave} disabled={disableAddUpdateButton()}>
            {(user != null) ? 'Update' : 'Add'}
          </button>
          {(user != null) && (onDelete != null) && (
            <button className='delete-button' onClick={onDelete}>
              Delete
            </button>
          )}
          <button className='add-update-button' onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

const RenderPasswordCriteria: React.FC<{ passwordValidation: {
  hasNumber: boolean
  hasSpecialChar: boolean
  hasCapitalLetter: boolean
  hasLowercaseLetter: boolean
  hasAtLeastEightChars: boolean
} }> = ({ passwordValidation }) => {
  const criteria = [
    { label: 'Contains a number', valid: passwordValidation.hasNumber },
    { label: 'Contains a special character', valid: passwordValidation.hasSpecialChar },
    { label: 'Contains a capital letter', valid: passwordValidation.hasCapitalLetter },
    { label: 'Contains a lowercase letter', valid: passwordValidation.hasLowercaseLetter },
    { label: 'Should be at least 8 characters long', valid: passwordValidation.hasAtLeastEightChars }
  ]
  const allValid = criteria.every((criterion) => criterion.valid)

  return (
    allValid
      ? <></>
      : (
        <div className='password-criteria'>
          {criteria.map((criterion, index) => (
            !criterion.valid && <p key={index} className='invalid-criterion'>{criterion.label}</p>
          ))}
        </div>)

  )
}

export default EditUserModal
